import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'
import type { BrokerAccount, BrokerAccountChange } from '../queries/broker.ts'

/**
 * 브로커 계정 · 모듈 브로커 쓰기 — `IFC-RO-71`~`74` · `IFC-RO-78` PUT · `IFC-RO-79`
 * (`ctcells-core/app/views/broker_accounts.py` · core@d50b8cf4).
 *
 * | 연산 | 경로 | 본문 | 응답 |
 * | --- | --- | --- | --- |
 * | 역할 바꾸기 | `PUT /broker/accounts/{name}/roles` | `command_id` + `module_ids`(**붙일 모듈 전부** · 빈 배열 = 다 뗀다) | 200 `BrokerAccount` |
 * | 끄기 · 켜기 | `POST /broker/accounts/{name}/disable` · `/enable` | `command_id` 만 | 200 `BrokerAccount` |
 * | 비밀번호 | `PUT /broker/accounts/{name}/password` | `command_id` 만 — 🔴 **서버가 만든다**(사람이 고르지 않는다) | 202 `BrokerAccountChange` · `password` **한 번만** |
 * | 지우기 | `DELETE /broker/accounts/{name}` | `command_id` 만 — **사유 칸이 없다**(계약 `CommandEnvelope`) | 202 `BrokerAccountChange`(끄기 + 관찰 → 시한 판정이 마저 지운다) |
 * | 모듈 접속 바꾸기 | `PUT /modules/{id}/broker` | `command_id` + `host` · `port` · `confirm_address_change` | 202 `BrokerAccountChange`(kind `connection`) |
 * | 모듈 ID 바꾸기 | `POST /modules/{id}/identity` | `command_id` + `new_module_id` · `confirm_module_id`(지금 ID 다시 입력) · `reason` | 202 `BrokerAccountChange`(kind `identity`) |
 *
 * - 역할: **admin 한정**(`ROLES`) · 🔴 제어락 비대상 — 서버가 `require_control_lock` 을 걸지 않는다(`controlToken.ts` 표에 넣지 않는다).
 * - 🔴 거절은 409 `BROKER_ACCOUNT_CHANGE_REJECTED` 하나에 `context.reason` 이 가른다(`batch_active` · `change_in_progress` ·
 *   `protected_account` · `module_not_acknowledged`). 화면 문면은 `@ctcells/ui` 의 `brokerFailure` 가 고른다.
 * - 🔴 비밀번호 값은 **응답에서 화면으로만** 간다 — 로그 · 저장소 · 캐시에 넣지 않는다. 그래서 이 훅들은 결과를 쿼리 캐시에
 *   쓰지 않고(무효화만) 컨테이너가 `mutateAsync` 의 반환을 창 하나에만 들고 있다.
 * - ⚠ 비밀번호 · 접속 · ID 는 모듈에 먼저 알리고(최대 5 s) 브로커를 바꾼다 — 시한은 명령 시한(`REQUEST_TIMEOUT_MS.command`).
 */

const ACCOUNTS = '/broker/accounts'

function accountPath(name: string, tail = ''): string {
  return `${ACCOUNTS}/${encodeURIComponent(name)}${tail}`
}

export const useSetBrokerAccountRoles = createCommand<
  { name: string; moduleIds: readonly string[]; commandId?: string },
  BrokerAccount
>({
  send: ({ name, moduleIds, commandId }) =>
    orchestrator.request<BrokerAccount>({
      method: 'PUT',
      path: accountPath(name, '/roles'),
      // 🔴 전체 교체다 — 받은 `module_ids` 위에 고친 것을 얹은 «전부»를 보낸다(빠진 것은 떼어진다)
      body: { command_id: commandId ?? newCommandId(), module_ids: [...moduleIds] },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.broker.all()],
})

export const useSetBrokerAccountDisabled = createCommand<
  { name: string; disabled: boolean; commandId?: string },
  BrokerAccount
>({
  send: ({ name, disabled, commandId }) =>
    orchestrator.request<BrokerAccount>({
      method: 'POST',
      path: accountPath(name, disabled ? '/disable' : '/enable'),
      body: { command_id: commandId ?? newCommandId() },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.broker.all()],
})

export const useChangeBrokerPassword = createCommand<
  { name: string; commandId?: string },
  BrokerAccountChange
>({
  send: ({ name, commandId }) =>
    orchestrator.request<BrokerAccountChange>({
      method: 'PUT',
      path: accountPath(name, '/password'),
      body: { command_id: commandId ?? newCommandId() },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.broker.all()],
})

export const useDeleteBrokerAccount = createCommand<
  { name: string; commandId?: string },
  BrokerAccountChange
>({
  send: ({ name, commandId }) =>
    orchestrator.request<BrokerAccountChange>({
      method: 'DELETE',
      path: accountPath(name),
      body: { command_id: commandId ?? newCommandId() },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.broker.all()],
})

export const useChangeModuleBrokerConnection = createCommand<
  {
    moduleId: string
    host: string
    port: number
    confirmAddressChange: boolean
    commandId?: string
  },
  BrokerAccountChange
>({
  send: ({ moduleId, host, port, confirmAddressChange, commandId }) =>
    orchestrator.request<BrokerAccountChange>({
      method: 'PUT',
      path: `/modules/${encodeURIComponent(moduleId)}/broker`,
      body: {
        command_id: commandId ?? newCommandId(),
        host,
        port,
        confirm_address_change: confirmAddressChange,
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.broker.all()],
})

export const useChangeModuleIdentity = createCommand<
  {
    moduleId: string
    newModuleId: string
    confirmModuleId: string
    reason: string
    commandId?: string
  },
  BrokerAccountChange
>({
  send: ({ moduleId, newModuleId, confirmModuleId, reason, commandId }) =>
    orchestrator.request<BrokerAccountChange>({
      method: 'POST',
      path: `/modules/${encodeURIComponent(moduleId)}/identity`,
      body: {
        command_id: commandId ?? newCommandId(),
        new_module_id: newModuleId,
        confirm_module_id: confirmModuleId,
        reason,
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  // 레지스트리 대체는 새 ID 가 도착할 때(서버 `settle`) — 모듈 목록도 같이 다시 묻는다
  invalidates: () => [keys.broker.all(), keys.modules.all()],
})
